import React, { useState, useContext, useEffect, useRef, useCallback } from "react"
import "./homepages.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBook, faCompass, faPlus, faTrash, faSpinner, faClock } from "@fortawesome/pro-regular-svg-icons"
import { useParams } from "@reach/router"
import { Link } from "gatsby"
import useModuleDB from "./shared/useModuleDB.js"
import { FirebaseContext } from "../Firebase"
import LoadingSpinner from "../../components/loading/LoadingSpinner.js"
import { useIntl } from "react-intl"
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons"
import { Modal } from "react-bootstrap"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import DOMPurify from "dompurify"
import { hasHTMLContent } from "./shared/utils.js"
import { RadioButton } from "primereact/radiobutton"
import { ImageWithFallback } from "./views/display.js"
import { FormattedMessage } from "react-intl"
import "primereact/resources/themes/lara-light-blue/theme.css"
import "primereact/resources/primereact.min.css"
import "primeicons/primeicons.css"
import { useQueryParam, StringParam } from "use-query-params"
import { navigate } from "gatsby"
import useModuleAuthorization from "./shared/useModuleAuthorization"
import { WingsButton, WingsModal } from "@wingspans-ui"
import { sendStandardToast } from "../toasts/standard-toast"

// can be rendered from module page or preview
export const ModuleHomeDisplay = ({ moduleData, moduleId, isPreview, userModuleData }) => {
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0)
  const [programId] = useQueryParam("program", StringParam)
  const [previewParam] = useQueryParam("preview", StringParam)
  const { locale } = useIntl()

  const { module_json } = moduleData || {}

  const {
    skills = [],
    skillsEs = [],
    quote,
    quoteEs,
    title,
    titleEs,
    category,
    categoryEs,
    image,
    quoteAuthor,
    fullDescription,
    fullDescriptionEs,
    shortDescription,
    shortDescriptionEs,
    bannerColor,
    sections = [],
  } = module_json || {}

  const localizedTitle = locale === "es-ES" ? titleEs : title
  const localizedShortDescription = locale === "es-ES" ? shortDescriptionEs : shortDescription
  const localizedFullDescription = locale === "es-ES" ? fullDescriptionEs : fullDescription
  const localizedQuote = locale === "es-ES" ? quoteEs : quote
  const localizedSkills = locale === "es-ES" ? skillsEs : skills
  const localizedCategory = locale === "es-ES" ? categoryEs : category
  const localizedSelectedSectionDescription =
    locale === "es-ES" ? sections[selectedSectionIndex]?.sectionDescriptionEs : sections[selectedSectionIndex]?.sectionDescription
  const localizedSelectedSectionDirections =
    locale === "es-ES" ? sections[selectedSectionIndex]?.sectionDirectionsEs : sections[selectedSectionIndex]?.sectionDirections

  const renderGradientClassName = () => {
    switch (bannerColor) {
      case "blue":
        return "blue-gradient"
      case "red":
        return "red-gradient"
      case "orange":
        return "orange-gradient"
      case "yellow":
        return "yellow-gradient"
      case "green":
        return "green-gradient"
      case "purple":
        return "purple-gradient"
      default:
        return "blue-gradient"
    }
  }

  const renderButtonText = () => {
    if (isPreview || !!previewParam) {
      return "Preview Module"
      // return <FormattedMessage id="curriculum-get-started" defaultMessage="Get Started" />
    }

    if (!!programId) {
      return <FormattedMessage id="curriculum-go-to-module" defaultMessage="Go To Module" />
    }

    switch (userModuleData?.status) {
      case "in_progress":
        return <FormattedMessage id="curriculum-continue-module" defaultMessage="Continue" />
      case "completed":
        return <FormattedMessage id="curriculum-restart-module" defaultMessage="Restart" />
      default:
        return <FormattedMessage id="curriculum-get-started" defaultMessage="Get Started" />
    }
  }

  return (
    <div className="curriculum-home module">
      <div className={`home-banner ${renderGradientClassName()}`}>
        <div className="inner">
          <div className="home-container">
            <div className="home-header">
              <label className="home-label">{localizedCategory}</label>
              <h2 className="home-title">{localizedTitle}</h2>
              <p className="home-description">{localizedShortDescription}</p>
              <ImageWithFallback className="home-image" src={image} />
            </div>
            <div className="home-cta">
              <div>
                <h6>
                  <FormattedMessage id="curriculum-you-can-do-it" defaultMessage="You can do it!" />
                </h6>
                <p>
                  <FormattedMessage id="curriculum-one-step-closer" defaultMessage="You're one step closer to being career ready." />
                </p>
              </div>

              {/* <Link to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${moduleId}/interactive`} style={{ pointerEvents: moduleId ? "auto" : "none" }}> */}
              <Link
                to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${moduleId}/interactive${isPreview || !!previewParam ? "?preview=1" : ""}${
                  !!programId ? `/?program=${programId}` : ""
                }`}
                style={{ pointerEvents: moduleId ? "auto" : "none" }}
              >
                <WingsButton variant="primary" size="small">
                  {renderButtonText()}
                </WingsButton>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="home-container inner">
        <div className="home-content">
          <div className="home-why-complete">
            <h3>
              <FormattedMessage id="curriculum-why-complete-this-module" defaultMessage="Why complete this module" />
            </h3>
            {localizedQuote && (
              <>
                <blockquote>"{localizedQuote}"</blockquote>
                <label>-{quoteAuthor}</label>
              </>
            )}

            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedFullDescription) }} />
          </div>
          {localizedSkills.length > 0 && (
            <div className="home-skills">
              <h6>
                <FormattedMessage id="curriculum-skills-you-will-develop" defaultMessage="Skills you will develop:" />
              </h6>
              <div className="home-skills-list">
                {localizedSkills.map((skill, index) => (
                  <div key={index} className="home-skill">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="home-tasks">
          <div className="home-tasks-header">
            <h3>
              <FormattedMessage id="curriculum-tasks-in-module" defaultMessage="Tasks in this module" />
            </h3>
          </div>
          <div className="home-tasks-container">
            <aside className="home-tasks-aside">
              {sections.map(({ sectionTitle, sectionTitleEs }, index) => (
                <div
                  key={index}
                  className={`home-task ${selectedSectionIndex === index ? "selected" : ""}`}
                  onClick={() => setSelectedSectionIndex(index)}
                >
                  <div className="home-task-number">{index + 1}</div>

                  <div>{locale === "es-ES" ? sectionTitleEs : sectionTitle}</div>
                </div>
              ))}
            </aside>
            <div className="home-task-content">
              <div className="home-task-content-inner">
                <h5>
                  <FormattedMessage id="curriculum-overview" defaultMessage="Overview" />
                </h5>
                {sections[selectedSectionIndex]?.sectionTime && (
                  <span>
                    {" "}
                    <FontAwesomeIcon icon={faClock} color="#63C0D5" />
                    {sections[selectedSectionIndex]?.sectionTime}
                  </span>
                )}
                <div className="home-task-overview">
                  <div className="home-task-overview-icon">
                    <FontAwesomeIcon icon={faBook} color="#63C0D5" size="2x" />
                  </div>
                  <div>
                    <label>
                      <FormattedMessage id="curriculum-what-you-will-learn" defaultMessage="What you will learn" />
                    </label>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedSelectedSectionDescription) }} />
                  </div>
                </div>

                {hasHTMLContent(localizedSelectedSectionDirections) && (
                  <div className="home-task-overview" style={{ marginTop: "1rem" }}>
                    <div className="home-task-overview-icon">
                      <FontAwesomeIcon icon={faCompass} color="#63C0D5" size="2x" />
                    </div>
                    <div>
                      <label>
                        <FormattedMessage id="curriculum-directions" defaultMessage="Directions" />
                      </label>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(localizedSelectedSectionDirections) }} />
                    </div>
                  </div>
                )}
              </div>
              <div className="home-task-footer">
                <Link
                  to={`${locale === "es-ES" ? "/es-ES" : ""}/modules/${moduleId}/interactive${isPreview || !!previewParam ? "?preview=1" : ""}${
                    !!programId ? `/?program=${programId}` : ""
                  }`}
                  style={{ pointerEvents: moduleId ? "auto" : "none" }}
                >
                  <WingsButton variant="primary" size="mini">
                    {renderButtonText()}
                  </WingsButton>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Load from page if
export const ModuleHome = ({ isPreview }) => {
  const { moduleId } = useParams()
  const { firebase, user, loading, isUserLoggedOut } = useContext(FirebaseContext)
  const { baseModuleTemplate, isModuleLoading, userModuleData } = useModuleDB(moduleId, firebase)

  // Prevent redirect loop: only navigate once per mount
  useEffect(() => {
    if (isUserLoggedOut) {
      navigate(`/login/?redirectTo=/modules/${moduleId}`, { replace: true })
    }
  }, [isUserLoggedOut])

  if (!baseModuleTemplate && !isModuleLoading) {
    return (
      <div>Module was not found</div>
    )
  }

  if (isModuleLoading || !firebase) {
    return <LoadingSpinner />
  }

  if (!baseModuleTemplate) {
    return <div>Module was not found</div>
  }

  return <ModuleHomeDisplay moduleData={baseModuleTemplate} moduleId={moduleId} isPreview={isPreview} userModuleData={userModuleData} />
}

export const EducatorModuleHome = () => {
  const { moduleId } = useParams()
  const { firebase, profile, schoolMemberships, subdomainConfig, loading } = useContext(FirebaseContext)
  const { baseModuleTemplate, isModuleLoading, userSQLId, error } = useModuleDB(moduleId, firebase)
  const moduleAuthorization = useModuleAuthorization({
    moduleTemplate: baseModuleTemplate,
    userSQLId,
    profile,
    schoolMemberships,
    subdomainSchoolId: subdomainConfig?.subdomainSchoolId,
  })
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleAssignClick = () => {
    setShowAssignModal(true)
  }

  const handleCloseModal = () => {
    setShowAssignModal(false)
  }

  const handleConfirmDelete = async () => {
    setIsDeleting(true)
    try {
      await firebase.deleteModuleById(moduleId)
      setShowDeleteModal(false)
      navigate("/educator/assignments/modules/?tab=LIBRARY")
    } catch (err) {
      console.error("Error deleting module", err)
      sendStandardToast({ heading: "Error deleting module", message: err?.message ?? "Please try again or contact Wingspans support." })
    } finally {
      setIsDeleting(false)
    }
  }

  if (loading || isModuleLoading) {
    return <LoadingSpinner />
  }

  if (!firebase) {
    return <div>Unable to load module</div>
  }

  if (error) {
    return <div>Unable to load module</div>
  }

  if (!baseModuleTemplate) {
    return <div>Module was not found</div>
  }

  if (!moduleAuthorization.canViewModule) {
    return <div>{moduleAuthorization.getUnauthorizedViewMessage()}</div>
  }

  const { module_json: { title, category, sections = [] } = {} } = baseModuleTemplate || {}

  const SectionTimeline = () => (
    <div className="section-timeline">
      <h5>{title}</h5>
      <label style={{ marginBottom: "2rem" }}>{category}</label>
      {sections.map(({ sectionTitle }, index) => (
        <div key={index} className="section-timeline-item">
          <div className="section-timeline-item-number">{index + 1}</div>
          <div className="section-timeline-item-title">{sectionTitle}</div>
        </div>
      ))}
    </div>
  )

  return (
    <>
      <div className="module-creation-page">
        <div className="module-creation-page-navigation">
          <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
            <div>
              <Link to={"/educator/assignments/modules"}>
                <span>
                  <FontAwesomeIcon icon={faArrowLeft} size="md" /> Assignments
                </span>{" "}
              </Link>
              | Assignment Overview
            </div>

            <div className="module-creation-page-navigation-actions">
              {moduleAuthorization.canDeleteModule && (
                <WingsButton variant="tertiary" size="mini" onClick={() => setShowDeleteModal(true)}>
                  Delete
                </WingsButton>
              )}
              {moduleAuthorization.canEditModule && (
                <Link to={`/educator/assignments/modules/edit/${moduleId}`}>
                  <WingsButton variant="tertiary" size="mini">
                    Edit
                  </WingsButton>
                </Link>
              )}
              {/* // new tab link */}
              <a href={`/modules/${moduleId}/interactive?preview=1`} target="_blank" rel="noopener noreferrer">
                <WingsButton variant="tertiary" size="mini">
                  Preview Module
                </WingsButton>
              </a>
              <WingsButton variant="primary" size="mini" onClick={handleAssignClick}>
                Assign
              </WingsButton>
            </div>
          </div>
        </div>

        <div className={`module-creation-page-content`}>
          {/* <div className="module-creation-page-sidebar outline educator-section-timeline">
            <SectionTimeline />
          </div> */}

          <div className="module-creation-page-main">
            <ModuleHome isPreview={true} />
          </div>
        </div>
      </div>

      <AssignModuleModal show={showAssignModal} onClose={handleCloseModal} moduleId={moduleId} title={title} />

      <WingsModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="sm"
        title="Delete Module"
        dismissOnMask={!isDeleting}
        dismissOnEscape={!isDeleting}
        secondaryActionLabel="Cancel"
        onSecondaryAction={() => setShowDeleteModal(false)}
        secondaryActionDisabled={isDeleting}
        primaryActionLabel={isDeleting ? "Deleting..." : "Delete"}
        onPrimaryAction={handleConfirmDelete}
        primaryActionDisabled={isDeleting}
      >
        {`Are you sure you want to delete ${title ? `"${title}"` : "this module"}? This action cannot be undone.`}
      </WingsModal>
    </>
  )
}

export const AssignModuleModal = ({ show, onClose, moduleId, title }) => {
  const { firebase, profile } = useContext(FirebaseContext)

  const [dueDate, setDueDate] = useState(null)
  const [assigneeType, setAssigneeType] = useState("INDIVIDUAL")
  const [allUsersList, setAllUsersList] = useState([])
  const [allGroupsList, setAllGroupsList] = useState([])
  const [searchUsers, setSearchUsers] = useState("")
  const [searchGroups, setSearchGroups] = useState("")
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [userPage, setUserPage] = useState(1)
  const [groupPage, setGroupPage] = useState(1)
  const ITEMS_PER_PAGE = 50

  // Refs for infinite scroll
  const userLoadMoreRef = useRef(null)
  const groupLoadMoreRef = useRef(null)

  useEffect(() => {
    // Only load data when modal is shown and firebase is available
    if (show && firebase) {
      const loadAllUsersAndGroups = async () => {
        try {
          setIsLoadingUsers(true)
          const userList = await firebase.getAllUserListing({ role: "student" })
          setAllUsersList(userList?.data?.users || [])
        } catch (error) {
          console.error("Error fetching users:", error)
        } finally {
          setIsLoadingUsers(false)
        }

        try {
          setIsLoadingGroups(true)
          const groupList = await firebase.groupsListCreatedByUser()
          setAllGroupsList(groupList?.data || [])
        } catch (error) {
          console.error("Error fetching groups:", error)
        } finally {
          setIsLoadingGroups(false)
        }
      }

      loadAllUsersAndGroups()
    }
  }, [show, firebase])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setDueDate(null)
      setAssigneeType("INDIVIDUAL")
      setSearchUsers("")
      setSearchGroups("")
      setSelectedUsers([])
      setSelectedGroups([])
      setAllUsersList([])
      setAllGroupsList([])
      setIsSubmitting(false)
      setIsLoadingUsers(false)
      setIsLoadingGroups(false)
      setUserPage(1)
      setGroupPage(1)
    }
  }, [show])

  const handleConfirmAssign = async () => {
    if (!dueDate) {
      alert("Due date is required, please select a date")
      return
    }

    if (isSubmitting) {
      // Prevent multiple submissions if already submitting
      return
    }

    try {
      setIsSubmitting(true)
      const groupPromises = selectedGroups.map((group) => {
        const payload = {
          assignedGroupId: group.id,
          assignedUserId: null,
          moduleId,
          dueDate,
          assigneeType: "GROUP",
          subdomain: window.location.hostname.split(".")[0] || "www",
          domain: window.location.hostname.split(".")[1] || "wingspans",
          educatorName: `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`,
        }
        return firebase.createModuleAssignment(payload)
      })

      const individualPromises = selectedUsers.map((user) => {
        const payload = {
          assignedGroupId: null,
          assignedUserId: user.id,
          moduleId,
          dueDate,
          assigneeType: "INDIVIDUAL",
          subdomain: window.location.hostname.split(".")[0] || "www",
          domain: window.location.hostname.split(".")[1] || "wingspans",
          educatorName: `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`,
        }
        return firebase.createModuleAssignment(payload)
      })

      await Promise.all([...groupPromises, ...individualPromises])
      onClose()
    } catch (error) {
      console.error("Error assigning module:", error)
      alert("Error assigning module, please contact Wingspans support")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddUser = (user) => {
    if (!selectedUsers.some((selectedUser) => selectedUser.id === user.id)) {
      setSelectedUsers([...selectedUsers, user])
    }
  }

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter((user) => user.id !== userId))
  }

  const handleAddGroup = (group) => {
    if (!selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)) {
      setSelectedGroups([...selectedGroups, group])
    }
  }

  const handleRemoveGroup = (groupId) => {
    setSelectedGroups(selectedGroups.filter((group) => group.id !== groupId))
  }

  const filteredUsers = allUsersList.filter((user) => {
    const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.toLowerCase()
    const email = user?.email?.toLowerCase() ?? ""
    return fullName.includes(searchUsers.toLowerCase()) || email.includes(searchUsers.toLowerCase())
  })

  const filteredGroups = allGroupsList.filter((group) => {
    const groupName = group?.name?.toLowerCase() ?? ""
    return groupName.includes(searchGroups.toLowerCase())
  })

  // Pagination for performance
  const paginatedUsers = filteredUsers.slice(0, userPage * ITEMS_PER_PAGE)
  const paginatedGroups = filteredGroups.slice(0, groupPage * ITEMS_PER_PAGE)
  const hasMoreUsers = filteredUsers.length > userPage * ITEMS_PER_PAGE
  const hasMoreGroups = filteredGroups.length > groupPage * ITEMS_PER_PAGE

  const loadMoreUsers = useCallback(() => setUserPage((prev) => prev + 1), [])
  const loadMoreGroups = useCallback(() => setGroupPage((prev) => prev + 1), [])

  // Infinite scroll observers
  useEffect(() => {
    const userElement = userLoadMoreRef.current
    if (!userElement || !hasMoreUsers || assigneeType !== "INDIVIDUAL") return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreUsers && assigneeType === "INDIVIDUAL") {
          loadMoreUsers()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(userElement)
    return () => observer.disconnect()
  }, [hasMoreUsers, loadMoreUsers, assigneeType])

  useEffect(() => {
    const groupElement = groupLoadMoreRef.current
    if (!groupElement || !hasMoreGroups || assigneeType !== "GROUP") return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreGroups && assigneeType === "GROUP") {
          loadMoreGroups()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(groupElement)
    return () => observer.disconnect()
  }, [hasMoreGroups, loadMoreGroups, assigneeType])

  // Reset pagination when search changes
  const handleUserSearch = (e) => {
    setSearchUsers(e.target.value)
    setUserPage(1)
  }

  const handleGroupSearch = (e) => {
    setSearchGroups(e.target.value)
    setGroupPage(1)
  }

  // Reset pagination when assignee type changes
  useEffect(() => {
    setUserPage(1)
    setGroupPage(1)
  }, [assigneeType])

  return (
    <Modal show={show} onHide={onClose} centered size="xl">
      <Modal.Header closeButton>
        <h4 style={{ marginBottom: "0" }}>Assign Module</h4>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: "2rem", marginBottom: "1rem" }}>
          <div style={{ flex: "1" }}>
            <label>Assignment Name</label>
            <h6 style={{ marginBottom: "0.5rem" }}>{title}</h6>
          </div>
          <div style={{ flex: "1" }}>
            <label>Due Date</label>
            <DatePicker
              selected={dueDate}
              onChange={(date) => setDueDate(date)}
              dateFormat="MMMM d, yyyy"
              placeholderText="Select a due date"
              className="form-control"
            />
          </div>
        </div>

        <label>Assign to</label>
        <div style={{ display: "flex", gap: "2rem", margin: "0.5rem 0", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="assigneeTypeIndividual"
              name="assigneeType"
              value="INDIVIDUAL"
              onChange={(e) => setAssigneeType(e.value)}
              checked={assigneeType === "INDIVIDUAL"}
            />
            <label htmlFor="assigneeTypeIndividual" style={{ marginBottom: 0 }}>
              Individual
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="assigneeTypeGroup"
              name="assigneeType"
              value="GROUP"
              onChange={(e) => setAssigneeType(e.value)}
              checked={assigneeType === "GROUP"}
            />
            <label htmlFor="assigneeTypeGroup" style={{ marginBottom: 0 }}>
              Group
            </label>
          </div>
        </div>

        {assigneeType === "INDIVIDUAL" && (
          <div className="user-dropdown-search">
            <div style={{ marginBottom: "0.5rem" }}>
              <label>Search Users</label>
              <input
                className="form-control"
                type="text"
                placeholder="Search user by name or email"
                value={searchUsers}
                onChange={handleUserSearch}
                style={{ maxWidth: "375px" }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
              <label style={{ flex: "1", marginBottom: "0" }}>All Users</label>
              <label style={{ flex: "1", marginBottom: "0" }}>Added Users</label>
            </div>
            <div className="user-dropdown-search-lists-container" style={{ display: "flex", gap: "1rem", height: "300px" }}>
              <div className="user-dropdown-search-list" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Add</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {isLoadingUsers ? (
                    <li style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                      <FontAwesomeIcon icon={faSpinner} spin size="lg" color="#40777c" />
                    </li>
                  ) : (
                    <>
                      {paginatedUsers.map((user) => (
                        <li key={user.id} onClick={() => handleAddUser(user)}>
                          <div>
                            <div>
                              {user.first_name} {user.last_name}
                            </div>
                            <small style={{ color: "#666", fontSize: "0.85em" }}>{user.email}</small>
                          </div>
                          <a>
                            <FontAwesomeIcon icon={faPlus} color="#40777c" />
                          </a>
                        </li>
                      ))}
                      {hasMoreUsers && (
                        <li
                          ref={userLoadMoreRef}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "1rem",
                            borderTop: "1px solid #eee",
                            minHeight: "60px",
                            alignItems: "center",
                          }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin size="sm" color="#40777c" />
                          <span style={{ marginLeft: "0.5rem", color: "#666", fontSize: "0.9em" }}>Loading more users...</span>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>

              <div className="user-dropdown-search-list white" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Remove</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {selectedUsers.map((user) => (
                    <li key={user.id} onClick={() => handleRemoveUser(user.id)}>
                      <div>
                        <div>
                          {user.first_name} {user.last_name}
                        </div>
                        <small style={{ color: "#666", fontSize: "0.85em" }}>{user.email}</small>
                      </div>
                      <a>
                        <FontAwesomeIcon icon={faTrash} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {assigneeType === "GROUP" && (
          <div className="user-dropdown-search">
            <div style={{ marginBottom: "0.5rem" }}>
              <label>Search Groups</label>
              <input
                className="form-control"
                type="text"
                placeholder="Search group by name"
                value={searchGroups}
                onChange={handleGroupSearch}
                style={{ maxWidth: "375px" }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
              <label style={{ flex: "1", marginBottom: "0" }}>All Groups</label>
              <label style={{ flex: "1", marginBottom: "0" }}>Added Groups</label>
            </div>
            <div className="user-dropdown-search-lists-container" style={{ display: "flex", gap: "1rem", height: "300px" }}>
              <div className="user-dropdown-search-list" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Add</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {isLoadingGroups ? (
                    <li style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                      <FontAwesomeIcon icon={faSpinner} spin size="lg" color="#40777c" />
                    </li>
                  ) : (
                    <>
                      {paginatedGroups.map((group) => (
                        <li key={group.id} onClick={() => handleAddGroup(group)}>
                          <div>{group.name}</div>
                          <a>
                            <FontAwesomeIcon icon={faPlus} color="#40777c" />
                          </a>
                        </li>
                      ))}
                      {hasMoreGroups && (
                        <li
                          ref={groupLoadMoreRef}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "1rem",
                            borderTop: "1px solid #eee",
                            minHeight: "60px",
                            alignItems: "center",
                          }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin size="sm" color="#40777c" />
                          <span style={{ marginLeft: "0.5rem", color: "#666", fontSize: "0.9em" }}>Loading more groups...</span>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>

              <div className="user-dropdown-search-list white" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Remove</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {selectedGroups.map((group) => (
                    <li key={group.id} onClick={() => handleRemoveGroup(group.id)}>
                      <div>{group.name}</div>
                      <a>
                        <FontAwesomeIcon icon={faTrash} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <WingsButton variant="primary" size="mini" loading={isSubmitting} onClick={handleConfirmAssign}>
          Assign
        </WingsButton>
      </Modal.Footer>
    </Modal>
  )
}

export const AssignProgramModal = ({ show, onClose, programId, title }) => {
  const { firebase, profile } = useContext(FirebaseContext)

  const [dueDate, setDueDate] = useState(null)
  const [assigneeType, setAssigneeType] = useState("INDIVIDUAL")
  const [allUsersList, setAllUsersList] = useState([])
  const [allGroupsList, setAllGroupsList] = useState([])
  const [searchUsers, setSearchUsers] = useState("")
  const [searchGroups, setSearchGroups] = useState("")
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [userPage, setUserPage] = useState(1)
  const [groupPage, setGroupPage] = useState(1)
  const ITEMS_PER_PAGE = 50

  // Refs for infinite scroll
  const userLoadMoreRef = useRef(null)
  const groupLoadMoreRef = useRef(null)

  useEffect(() => {
    // Only load data when modal is shown and firebase is available
    if (show && firebase) {
      const loadAllUsersAndGroups = async () => {
        try {
          setIsLoadingUsers(true)
          const userList = await firebase.getAllUserListing({ role: "student" })
          setAllUsersList(userList?.data?.users || [])
        } catch (error) {
          console.error("Error fetching users:", error)
        } finally {
          setIsLoadingUsers(false)
        }

        try {
          setIsLoadingGroups(true)
          const groupList = await firebase.groupsListCreatedByUser("student")
          setAllGroupsList(groupList?.data || [])
        } catch (error) {
          console.error("Error fetching groups:", error)
        } finally {
          setIsLoadingGroups(false)
        }
      }

      loadAllUsersAndGroups()
    }
  }, [show, firebase])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setDueDate(null)
      setAssigneeType("INDIVIDUAL")
      setSearchUsers("")
      setSearchGroups("")
      setSelectedUsers([])
      setSelectedGroups([])
      setAllUsersList([])
      setAllGroupsList([])
      setIsSubmitting(false)
      setIsLoadingUsers(false)
      setIsLoadingGroups(false)
      setUserPage(1)
      setGroupPage(1)
    }
  }, [show])

  const handleConfirmAssign = async () => {
    if (!dueDate) {
      alert("Due date is required, please select a date")
      return
    }

    if (isSubmitting) {
      // Prevent multiple submissions if already submitting
      return
    }

    try {
      setIsSubmitting(true)
      const groupPromises = selectedGroups.map((group) => {
        const payload = {
          assignedGroupId: group.id,
          assignedUserId: null,
          programId,
          dueDate,
          assigneeType: "GROUP",
          subdomain: window.location.hostname.split(".")[0] || "www",
          domain: window.location.hostname.split(".")[1] || "wingspans",
          educatorName: `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`,
        }
        return firebase.createProgramAssignment(payload)
      })

      const individualPromises = selectedUsers.map((user) => {
        const payload = {
          assignedGroupId: null,
          assignedUserId: user.id,
          programId,
          dueDate,
          assigneeType: "INDIVIDUAL",
          subdomain: window.location.hostname.split(".")[0] || "www",
          domain: window.location.hostname.split(".")[1] || "wingspans",
          educatorName: `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`,
        }
        return firebase.createProgramAssignment(payload)
      })

      await Promise.all([...groupPromises, ...individualPromises])
      onClose()
    } catch (error) {
      console.error("Error assigning program:", error)
      alert("Error assigning program, please contact Wingspans support")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddUser = (user) => {
    if (!selectedUsers.some((selectedUser) => selectedUser.id === user.id)) {
      setSelectedUsers([...selectedUsers, user])
    }
  }

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter((user) => user.id !== userId))
  }

  const handleAddGroup = (group) => {
    if (!selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)) {
      setSelectedGroups([...selectedGroups, group])
    }
  }

  const handleRemoveGroup = (groupId) => {
    setSelectedGroups(selectedGroups.filter((group) => group.id !== groupId))
  }

  const filteredUsers = allUsersList.filter((user) => {
    const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.toLowerCase()
    const email = user?.email?.toLowerCase() ?? ""
    return fullName.includes(searchUsers.toLowerCase()) || email.includes(searchUsers.toLowerCase())
  })

  const filteredGroups = allGroupsList.filter((group) => {
    const groupName = group?.name?.toLowerCase() ?? ""
    return groupName.includes(searchGroups.toLowerCase())
  })

  // Pagination for performance
  const paginatedUsers = filteredUsers.slice(0, userPage * ITEMS_PER_PAGE)
  const paginatedGroups = filteredGroups.slice(0, groupPage * ITEMS_PER_PAGE)
  const hasMoreUsers = filteredUsers.length > userPage * ITEMS_PER_PAGE
  const hasMoreGroups = filteredGroups.length > groupPage * ITEMS_PER_PAGE

  const loadMoreUsers = useCallback(() => setUserPage((prev) => prev + 1), [])
  const loadMoreGroups = useCallback(() => setGroupPage((prev) => prev + 1), [])

  // Infinite scroll observers
  useEffect(() => {
    const userElement = userLoadMoreRef.current
    if (!userElement || !hasMoreUsers || assigneeType !== "INDIVIDUAL") return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreUsers && assigneeType === "INDIVIDUAL") {
          loadMoreUsers()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(userElement)
    return () => observer.disconnect()
  }, [hasMoreUsers, loadMoreUsers, assigneeType])

  useEffect(() => {
    const groupElement = groupLoadMoreRef.current
    if (!groupElement || !hasMoreGroups || assigneeType !== "GROUP") return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreGroups && assigneeType === "GROUP") {
          loadMoreGroups()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(groupElement)
    return () => observer.disconnect()
  }, [hasMoreGroups, loadMoreGroups, assigneeType])

  // Reset pagination when search changes
  const handleUserSearch = (e) => {
    setSearchUsers(e.target.value)
    setUserPage(1)
  }

  const handleGroupSearch = (e) => {
    setSearchGroups(e.target.value)
    setGroupPage(1)
  }

  // Reset pagination when assignee type changes
  useEffect(() => {
    setUserPage(1)
    setGroupPage(1)
  }, [assigneeType])

  return (
    <Modal show={show} onHide={onClose} centered size="xl">
      <Modal.Header closeButton>
        <h4 style={{ marginBottom: "0" }}>Assign Program</h4>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: "2rem", marginBottom: "1rem" }}>
          <div style={{ flex: "1" }}>
            <label>Assignment Name</label>
            <h6 style={{ marginBottom: "0.5rem" }}>{title}</h6>
          </div>
          <div style={{ flex: "1" }}>
            <label>Due Date</label>
            <DatePicker
              selected={dueDate}
              onChange={(date) => setDueDate(date)}
              dateFormat="MMMM d, yyyy"
              placeholderText="Select a due date"
              className="form-control"
            />
          </div>
        </div>

        <label>Assign to</label>
        <div style={{ display: "flex", gap: "2rem", margin: "0.5rem 0", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="programAssigneeTypeIndividual"
              name="programAssigneeType"
              value="INDIVIDUAL"
              onChange={(e) => setAssigneeType(e.value)}
              checked={assigneeType === "INDIVIDUAL"}
            />
            <label htmlFor="programAssigneeTypeIndividual" style={{ marginBottom: 0 }}>
              Individual
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="programAssigneeTypeGroup"
              name="programAssigneeType"
              value="GROUP"
              onChange={(e) => setAssigneeType(e.value)}
              checked={assigneeType === "GROUP"}
            />
            <label htmlFor="programAssigneeTypeGroup" style={{ marginBottom: 0 }}>
              Group
            </label>
          </div>
        </div>

        {assigneeType === "INDIVIDUAL" && (
          <div className="user-dropdown-search">
            <div style={{ marginBottom: "0.5rem" }}>
              <label>Search Users</label>
              <input
                className="form-control"
                type="text"
                placeholder="Search user by name or email"
                value={searchUsers}
                onChange={handleUserSearch}
                style={{ maxWidth: "375px" }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
              <label style={{ flex: "1", marginBottom: "0" }}>All Users</label>
              <label style={{ flex: "1", marginBottom: "0" }}>Added Users</label>
            </div>
            <div className="user-dropdown-search-lists-container" style={{ display: "flex", gap: "1rem", height: "300px" }}>
              <div className="user-dropdown-search-list" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Add</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {isLoadingUsers ? (
                    <li style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                      <FontAwesomeIcon icon={faSpinner} spin size="lg" color="#40777c" />
                    </li>
                  ) : (
                    <>
                      {paginatedUsers.map((user) => (
                        <li key={user.id} onClick={() => handleAddUser(user)}>
                          <div>
                            <div>
                              {user.first_name} {user.last_name}
                            </div>
                            <small style={{ color: "#666", fontSize: "0.85em" }}>{user.email}</small>
                          </div>
                          <a>
                            <FontAwesomeIcon icon={faPlus} color="#40777c" />
                          </a>
                        </li>
                      ))}
                      {hasMoreUsers && (
                        <li
                          ref={userLoadMoreRef}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "1rem",
                            borderTop: "1px solid #eee",
                            minHeight: "60px",
                            alignItems: "center",
                          }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin size="sm" color="#40777c" />
                          <span style={{ marginLeft: "0.5rem", color: "#666", fontSize: "0.9em" }}>Loading more users...</span>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>

              <div className="user-dropdown-search-list white" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Remove</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {selectedUsers.map((user) => (
                    <li key={user.id} onClick={() => handleRemoveUser(user.id)}>
                      <div>
                        <div>
                          {user.first_name} {user.last_name}
                        </div>
                        <small style={{ color: "#666", fontSize: "0.85em" }}>{user.email}</small>
                      </div>
                      <a>
                        <FontAwesomeIcon icon={faTrash} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {assigneeType === "GROUP" && (
          <div className="user-dropdown-search">
            <div style={{ marginBottom: "0.5rem" }}>
              <label>Search Groups</label>
              <input
                className="form-control"
                type="text"
                placeholder="Search group by name"
                value={searchGroups}
                onChange={handleGroupSearch}
                style={{ maxWidth: "375px" }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginBottom: "0.5rem" }}>
              <label style={{ flex: "1", marginBottom: "0" }}>All Groups</label>
              <label style={{ flex: "1", marginBottom: "0" }}>Added Groups</label>
            </div>
            <div className="user-dropdown-search-lists-container" style={{ display: "flex", gap: "1rem", height: "300px" }}>
              <div className="user-dropdown-search-list" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Add</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {isLoadingGroups ? (
                    <li style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                      <FontAwesomeIcon icon={faSpinner} spin size="lg" color="#40777c" />
                    </li>
                  ) : (
                    <>
                      {paginatedGroups.map((group) => (
                        <li key={group.id} onClick={() => handleAddGroup(group)}>
                          <div>{group.name}</div>
                          <a>
                            <FontAwesomeIcon icon={faPlus} color="#40777c" />
                          </a>
                        </li>
                      ))}
                      {hasMoreGroups && (
                        <li
                          ref={groupLoadMoreRef}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "1rem",
                            borderTop: "1px solid #eee",
                            minHeight: "60px",
                            alignItems: "center",
                          }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin size="sm" color="#40777c" />
                          <span style={{ marginLeft: "0.5rem", color: "#666", fontSize: "0.9em" }}>Loading more groups...</span>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>

              <div className="user-dropdown-search-list white" style={{ flex: "1", height: "100%" }}>
                <div className="user-dropdown-search-name">
                  <label className="form-label">Name</label>
                  <label className="form-label">Remove</label>
                </div>
                <ul style={{ height: "calc(100% - 30px)", overflowY: "auto" }}>
                  {selectedGroups.map((group) => (
                    <li key={group.id} onClick={() => handleRemoveGroup(group.id)}>
                      <div>{group.name}</div>
                      <a>
                        <FontAwesomeIcon icon={faTrash} color="#40777c" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <WingsButton variant="primary" size="mini" loading={isSubmitting} onClick={handleConfirmAssign}>
          Assign
        </WingsButton>
      </Modal.Footer>
    </Modal>
  )
}
